"use client"

import { Text } from "@modules/common/components/ui"
import { useMemo, useState } from "react"

const PREVIEW_LENGTH = 300

// Roughly the first paragraph: prefer the first paragraph break when it
// falls within the preview window, otherwise cut at a word boundary.
const getPreview = (text: string): string => {
  const paragraphBreak = text.indexOf("\n\n")

  if (paragraphBreak > 0 && paragraphBreak <= PREVIEW_LENGTH) {
    return text.slice(0, paragraphBreak)
  }

  if (text.length <= PREVIEW_LENGTH) {
    return text
  }

  const slice = text.slice(0, PREVIEW_LENGTH)
  const lastSpace = slice.lastIndexOf(" ")

  return slice.slice(0, lastSpace > 0 ? lastSpace : PREVIEW_LENGTH)
}

type CollapsibleDescriptionProps = {
  description: string
}

const CollapsibleDescription = ({
  description,
}: CollapsibleDescriptionProps) => {
  const [expanded, setExpanded] = useState(false)

  const preview = useMemo(() => getPreview(description), [description])
  const isTruncated = preview.length < description.length

  return (
    <div>
      <Text
        className="text-medium text-ui-fg-subtle whitespace-pre-line"
        data-testid="product-description"
      >
        {expanded || !isTruncated ? description : `${preview}…`}
      </Text>
      {isTruncated && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 text-small-regular font-medium text-ui-fg-interactive hover:underline"
          data-testid="product-description-toggle"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  )
}

export default CollapsibleDescription
